/**
 * The table's layout state, and how it persists.
 *
 * Two things are kept: what the user is **looking at** (sort, filters, search,
 * page) and how they have **arranged** the table (column order, visibility,
 * pinning, widths, page size).
 *
 * Only the arrangement is saved. Sort and filters live in the URL instead, so a
 * narrowed view is a link somebody can send — and so opening a register fresh
 * shows everything rather than whatever was filtered three weeks ago, which is
 * the state people mistake for missing data.
 *
 * The save model has two modes and the distinction is deliberate:
 *
 * - **auto** — every arrangement change persists immediately;
 * - **manual** — changes are held, `unsaved` goes true, and `saveNow()` commits.
 *
 * The auto/manual toggle is itself a preference: it persists immediately and is
 * never part of the pending queue, so flipping it never counts as a change. That
 * sounds pedantic until the alternative is a table that reports itself unsaved
 * because you looked at the save menu.
 */

import { useCallback, useRef, useState } from 'react';

import { loadJSON, saveJSON } from '../../lib/persist';
import type { SortDir } from './types';

export type PinSide = 'left' | 'right';

export interface TableLayout {
  /** Field order. Fields missing from this list keep their declared position. */
  order: string[];
  hidden: string[];
  pins: Record<string, PinSide>;
  widths: Record<string, number>;
  pageSize: number;
  /** Whether arrangement changes persist immediately. */
  autoSave: boolean;
}

const DEFAULT_LAYOUT: TableLayout = {
  order: [],
  hidden: [],
  pins: {},
  widths: {},
  pageSize: 25,
  autoSave: true,
};

/**
 * Serialize only the arrangement for the unsaved comparison.
 *
 * `autoSave` is excluded on purpose — see the module docstring.
 */
function arrangementOf(layout: TableLayout): string {
  return JSON.stringify({ ...layout, autoSave: undefined });
}

function migrate(stored: unknown): Partial<TableLayout> {
  // A stored layout outlives the version that wrote it, so every field is
  // checked rather than trusted. A layout that fails to parse falls back to the
  // default, which is a table that looks untouched — recoverable. A layout that
  // half-parses could hide every column, which is not.
  if (typeof stored !== 'object' || stored === null) return {};
  const value = stored as Record<string, unknown>;
  const out: Partial<TableLayout> = {};
  if (Array.isArray(value.order)) out.order = value.order.filter((f): f is string => typeof f === 'string');
  if (Array.isArray(value.hidden)) out.hidden = value.hidden.filter((f): f is string => typeof f === 'string');
  if (typeof value.pins === 'object' && value.pins !== null) {
    out.pins = Object.fromEntries(
      Object.entries(value.pins as Record<string, unknown>).filter(
        (entry): entry is [string, PinSide] => entry[1] === 'left' || entry[1] === 'right',
      ),
    );
  }
  if (typeof value.widths === 'object' && value.widths !== null) {
    out.widths = Object.fromEntries(
      Object.entries(value.widths as Record<string, unknown>).filter(
        (entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0,
      ),
    );
  }
  if (typeof value.pageSize === 'number' && value.pageSize > 0) out.pageSize = value.pageSize;
  if (typeof value.autoSave === 'boolean') out.autoSave = value.autoSave;
  return out;
}

export interface TableStateHandle {
  layout: TableLayout;
  /** Apply an arrangement change, persisting it when auto-save is on. */
  update: (patch: Partial<TableLayout>) => void;
  /** Commit the pending arrangement. */
  saveNow: () => void;
  /** Discard the pending arrangement and return to what was saved. */
  revert: () => void;
  /** Reset to the table's declared defaults. */
  reset: () => void;
  unsaved: boolean;
}

export function useTableState(
  key: string,
  options: { forceAutoSave?: boolean; defaultPageSize?: number } = {},
): TableStateHandle {
  const { forceAutoSave = false, defaultPageSize = 25 } = options;
  const storageKey = `table.${key}`;

  const [layout, setLayout] = useState<TableLayout>(() => ({
    ...DEFAULT_LAYOUT,
    pageSize: defaultPageSize,
    ...migrate(loadJSON<TableLayout>(storageKey)),
    // Where the product hides the control, saving is always on — including for
    // a user whose stored layout was written while the toggle existed and
    // happened to be off. Otherwise hiding the control silently strands them
    // with a layout that never persists and no way to switch it back.
    ...(forceAutoSave ? { autoSave: true } : {}),
  }));

  const savedRef = useRef<string>(arrangementOf(layout));
  const [unsaved, setUnsaved] = useState(false);

  const update = useCallback(
    (patch: Partial<TableLayout>) => {
      setLayout((previous) => {
        const next = { ...previous, ...patch };

        // The auto/manual preference persists on its own and never touches the
        // unsaved flag.
        if ('autoSave' in patch && patch.autoSave !== previous.autoSave) {
          if (next.autoSave) {
            // Switching auto-save ON also commits whatever was pending —
            // otherwise a user turns it on and their arrangement still is not
            // saved, which is the opposite of what the switch says.
            saveJSON(storageKey, next);
            savedRef.current = arrangementOf(next);
            setUnsaved(false);
          } else {
            // Switching it OFF persists only the preference. The saved
            // arrangement and anything pending are left exactly as they were.
            const stored = { ...(loadJSON<TableLayout>(storageKey) ?? next), autoSave: false };
            saveJSON(storageKey, stored);
            setUnsaved(arrangementOf(next) !== savedRef.current);
          }
          return next;
        }

        if (next.autoSave) {
          saveJSON(storageKey, next);
          savedRef.current = arrangementOf(next);
          setUnsaved(false);
        } else {
          setUnsaved(arrangementOf(next) !== savedRef.current);
        }
        return next;
      });
    },
    [storageKey],
  );

  const saveNow = useCallback(() => {
    setLayout((current) => {
      saveJSON(storageKey, current);
      savedRef.current = arrangementOf(current);
      setUnsaved(false);
      return current;
    });
  }, [storageKey]);

  const revert = useCallback(() => {
    const stored = migrate(loadJSON<TableLayout>(storageKey));
    setLayout((current) => {
      const next: TableLayout = {
        ...DEFAULT_LAYOUT,
        pageSize: defaultPageSize,
        ...stored,
        // The preference is not part of what gets reverted.
        autoSave: current.autoSave,
      };
      savedRef.current = arrangementOf(next);
      setUnsaved(false);
      return next;
    });
  }, [storageKey, defaultPageSize]);

  const reset = useCallback(() => {
    const next: TableLayout = {
      ...DEFAULT_LAYOUT,
      pageSize: defaultPageSize,
      autoSave: forceAutoSave ? true : layout.autoSave,
    };
    saveJSON(storageKey, next);
    savedRef.current = arrangementOf(next);
    setUnsaved(false);
    setLayout(next);
  }, [storageKey, defaultPageSize, forceAutoSave, layout.autoSave]);

  return { layout, update, saveNow, revert, reset, unsaved };
}

/**
 * Whether a register shows the save / auto-save control.
 *
 * The app says which it wants (`AppConfig.layoutSaving`) and a register may
 * override it; with neither, there is no control and everything is saved —
 * the answer that cannot lose anybody's arrangement.
 *
 * **Hiding the control never turns saving off.** That is the whole reason this
 * decision lives in one named function: "hidden" must not drift into "not
 * saved", which is invisible until somebody loses an arrangement.
 */
export function showsLayoutControl(
  configured: 'control' | 'always' | undefined,
  override?: 'control' | 'always',
): boolean {
  return (override ?? configured ?? 'always') === 'control';
}

/** Sort state as a map, which is how the header cells read it. */
export function sortMapOf(sort: readonly { field: string; dir: SortDir }[]): Record<string, SortDir> {
  return Object.fromEntries(sort.map((criterion) => [criterion.field, criterion.dir]));
}

/**
 * Cycle one column's sort: none → asc → desc → none.
 *
 * Returning to none matters. A tri-state cycle lets somebody undo a sort and
 * see the register's natural order again; a two-state toggle leaves them stuck
 * choosing between two orders neither of which is the default.
 *
 * Shift-clicking appends instead of replacing, which is how multi-sort is
 * reached without a separate dialog.
 */
export function cycleSort(
  current: readonly { field: string; dir: SortDir }[],
  field: string,
  additive: boolean,
): { field: string; dir: SortDir }[] {
  const existing = current.find((criterion) => criterion.field === field);

  if (!additive) {
    if (!existing) return [{ field, dir: 'asc' }];
    if (existing.dir === 'asc') return [{ field, dir: 'desc' }];
    return [];
  }

  if (!existing) return [...current, { field, dir: 'asc' }];
  if (existing.dir === 'asc') {
    return current.map((criterion) =>
      criterion.field === field ? { field, dir: 'desc' as SortDir } : criterion,
    );
  }
  return current.filter((criterion) => criterion.field !== field);
}
